import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@shared/providers/AuthContext';
import { useActiveSessionSync } from '@/features/sso/useActiveSessionSync';
import { isSsoSite } from '@/features/sso/ssoSession';

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to /{siteType}/login if not authenticated.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const { tenant } = useParams<{ tenant: string }>();
  const urlSiteType = tenant || 'medix';

  useActiveSessionSync(
    isSsoSite(urlSiteType)
      ? { site: urlSiteType, entryType: auth.authEntryType }
      : undefined,
    Boolean(auth?.isAuthenticated && !auth.isLoading),
  );

  if (!auth) {
    return <Navigate to={`/${urlSiteType}/login`} state={{ from: location }} replace />;
  }

  const { isAuthenticated, isLoading, clinic } = auth;

  // Site type from authenticated clinic
  const clinicSiteType = clinic?.site_type;
  const parentSiteType = clinic?.parent_site_type;

  // Clinic is required for tenant routes (useClinicContext). Do not render children until available.
  const clinicReady = Boolean(clinic?.site_type && clinic?.clinic_number);

  // Show loading while checking auth or until clinic (tenant context) is available
  if (isLoading || (isAuthenticated && !clinicReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{isLoading ? '読み込み中...' : '施設情報を読み込み中...'}</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={`/${urlSiteType}/login`} state={{ from: location }} replace />;
  }

  // Check if user is accessing a site within their tenant family.
  // Allow access when the clinic's own site_type or its parent_site_type
  // matches the URL tenant (mirrors backend ClinicUserAccessMiddleware family check).
  const belongsToFamily = clinicSiteType === urlSiteType || parentSiteType === urlSiteType;

  if (clinicSiteType && !belongsToFamily) {
    const redirectTarget = parentSiteType || clinicSiteType;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">アクセス権限がありません</h1>
          <p className="text-gray-600 mb-6">
            このサイトへのアクセス権限がありません。
            <br />
            ご利用のアカウントは「{redirectTarget}」サイト用です。
          </p>
          <a
            href={`/${redirectTarget}`}
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {redirectTarget}サイトへ移動
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
